import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import { Box, Button, InputAdornment, Link, OutlinedInput } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link as RouterLink, useOutletContext } from "react-router";
import FiltersList from "../../../Components/Filters/FiltersList/filters-list";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../Components/Misc/ListPageHeader";
import OrderItemPanel from "../../../Components/Panels/OrderItemPanel/order-item-panel";
import OrderItemTable, { OrderItemTableContext } from "../../../Components/Table/OrdersTable/orders-items-table";
import TableSkeleton from "../../../Components/Table/TableSkeleton";
import { cacheService, orderService, searchService } from "../../../Core/Api/services";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import { GenericSearchWithParametersRequest } from "../../../Core/Api/Types/generic-search-with-parameters-request";
import { MetadataFieldValueObjectTypes } from "../../../Core/Api/Types/metadata-field-value-object-types";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { GetFilters, GetTitleCustomerIdsFilter } from "../../../Redux/Reducers/redux-slice-filters";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { PlatformMode } from "../../../Core/Api/Types/platform-mode";

function OrderItemSearch() {
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState(true);
  const [openPanelOrderItem, setOpenPanelOrderItem] = useState(false);
  const [selectedOrderItemId, setSelectedOrderItemId] = useState<number>(-1);
  const [rows, setData] = useState<DbOrderItemInfos[]>([]);
  const [fields, setFields] = useState<DbMetadataFieldInfos[]>([]);
  const [display, setDisplay] = useState<"query" | "result">("query");
  const [pattern, setPattern] = useState<string>("");
  const isAdam = cacheService.GetPlatformMode() === PlatformMode.Adam;

  const filters = useSelector(GetFilters);
  const customerIdFilter = useSelector(GetTitleCustomerIdsFilter);
  const metadata = cacheService.GetMetadata();
  const enqueueCustomSnackbar = useCustomSnackbar();

  // Outlet context used to pass props to parent DashboardLayout through react router dom
  const { selectedItems, setSelectedItems } = useOutletContext<{
    setSelectedItems: React.Dispatch<React.SetStateAction<DbOrderItemInfos[]>>;
    selectedItems: DbOrderItemInfos[];
  }>();

  const hidePanelOrder = () => {
    setOpenPanelOrderItem(false);
  };
  const showPanelOrder = (e: any) => {
    setSelectedOrderItemId(e);
    setOpenPanelOrderItem(true);
  };

  // Handlers
  const handleSearch = useCallback(() => {
    setLoading(true);
    setDisplay("result");

    let request: GenericSearchWithParametersRequest = {
      parameters: [],
      pattern: pattern,
    };

    filters.forEach((filter) => {
      let isArray = Array.isArray(filter.value.value);

      request.parameters?.push({
        tagName: metadata.find((x) => x.id === filter.id)!.tagName,
        value: !isArray ? filter.value : filter.value?.value.map((v) => v),
      });
    });

    if (customerIdFilter) {
      request.parameters?.push({
        tagName: "titleCustomerId",
        value: customerIdFilter,
      });
    }

    searchService.SearchWithParameters(request).then(function (response) {
      if (response.items) setData(response.items);
      if (!response.success)
        enqueueCustomSnackbar(response.message || t("alert:error.somethingWentWrongWithDot"), "error");
      setLoading(false);
    });
  }, [filters, customerIdFilter, pattern]);

  // Load dedicated metadata fields
  useEffect(() => {
    orderService.GetMetadataFields().then((response) => {
      if (response.metadataFields) setFields(response.metadataFields);

      setLoading(false);
    });
  }, []);

  return (
    <>
      {display === "result" ? (
        <>
          {isLoading ? (
            <TableSkeleton />
          ) : (
            <>
              <Link
                color="white"
                underline="hover"
                component={RouterLink}
                to="#"
                sx={{ mt: 1 }}
                onClick={() => setDisplay("query")}
              >
                <ArrowBackRoundedIcon sx={{ mr: 0.5 }} />
                {t("button:backToQuery")}
              </Link>
              <OrderItemTable
                orders={rows}
                showPanelOrder={showPanelOrder}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                enableWebsocket={false}
                context={OrderItemTableContext.Search}
              />
            </>
          )}
        </>
      ) : (
        <>
          {isLoading ? (
            <TableSkeleton />
          ) : (
            <Box>
              <ListPageHeader mb={1}>
                <ListPageHeaderLeft title={t("component:listPageHeaderLeft.title.openFilterPanelToStart")} />
                <ListPageHeaderRight>
                  {isAdam && (
                    <OutlinedInput
                      placeholder="Search"
                      value={pattern}
                      endAdornment={
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      }
                      onChange={(e) => setPattern(e.target.value)}
                    />
                  )}
                  <Button variant="contained" color="primary" onClick={() => handleSearch()}>
                    {t("common:search")}
                  </Button>
                </ListPageHeaderRight>
              </ListPageHeader>
              <FiltersList
                sx={{ mb: 4 }}
                layer={ObjectLayer.Title}
                type={MetadataFieldValueObjectTypes.DbOrderItemInfos}
                metadataFieldsOverride={fields}
              />
            </Box>
          )}
        </>
      )}

      <OrderItemPanel open={openPanelOrderItem} onClose={hidePanelOrder} itemId={selectedOrderItemId} />
    </>
  );
}

export default OrderItemSearch;
